import React, { Context, createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { AuthModalEventDetail, AUTH_MODAL_EVENT, AuthModalIntent } from '../utils/auth-modal.events';
import { postAnalyticsEvent } from '../services/analytics.service';
import { ANALYTICS_EVENT_TYPES } from '../types/analytics.types';

interface AuthModalContextType {
    isOpen: boolean;
    intent: AuthModalIntent;
    openModal: (intent: AuthModalIntent, redirectUrl?: string) => void;
    closeModal: () => void;
}

interface AuthModalProviderProps {
    children: JSX.Element | JSX.Element[];
}

const AuthModalContext: Context<AuthModalContextType> = createContext<AuthModalContextType>(null!);

const AuthModalProvider = ({ children }: AuthModalProviderProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const [intent, setIntent] = useState<AuthModalIntent>('sign_in');

    const openModal = useCallback((nextIntent: AuthModalIntent, redirectUrl?: string) => {
        setIntent(nextIntent);
        try {
            if (redirectUrl) {
                localStorage.setItem('redirectAfterLogin', redirectUrl);
            } else if (nextIntent === 'sign_in') {
                localStorage.removeItem('redirectAfterLogin');
            }
        } catch {
            // Authentication must remain available when browser privacy settings block storage.
        }
        void postAnalyticsEvent({
            eventType: ANALYTICS_EVENT_TYPES.AUTH_MODAL_OPENED,
            metadata: {
                intent: nextIntent,
                hasRedirectUrl: Boolean(redirectUrl)
            }
        });
        setIsOpen(true);
    }, []);

    const closeModal = useCallback(() => {
        setIsOpen(false);
    }, []);

    useEffect(() => {
        const handler = (event: Event) => {
            const detail = (event as CustomEvent<AuthModalEventDetail>).detail;
            if (detail.action === 'open') {
                openModal(detail.intent, detail.redirectUrl);
                return;
            }
            closeModal();
        };

        window.addEventListener(AUTH_MODAL_EVENT, handler);
        return () => {
            window.removeEventListener(AUTH_MODAL_EVENT, handler);
        };
    }, [closeModal, openModal]);

    const value = useMemo(() => ({
        isOpen,
        intent,
        openModal,
        closeModal
    }), [closeModal, intent, isOpen, openModal]);

    return (
        <AuthModalContext.Provider value={value}>
            {children}
        </AuthModalContext.Provider>
    );
};

export const useAuthModal = () => {
    return useContext(AuthModalContext);
};

export default AuthModalProvider;
